import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { History, Loader2, RefreshCw } from "lucide-react";

const FIELD_LABELS = {
  price: "Price", price_label: "Price label", active: "Active", display_name: "Name",
  public_description: "Description", paypal_button_link: "PayPal link",
  lunipay_payment_link_url: "Processor link", features: "Bullets", sort_order: "Order",
  billing_mode: "Billing mode", currency: "Currency", lunipay_enabled: "Processor on",
  paypal_enabled: "PayPal on", processor_name: "Processor name",
};
const ACTION_LABELS = {
  products_write: "Edited", products_create: "Created", products_delete: "Deleted",
  tiers_write: "Tier edited", billing_settings_write: "Billing", payments_config_write: "Providers",
};

const show = (field, v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (field === "price") return `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  return String(v);
};

function ChangeList({ entry }) {
  if (!entry.changes) return <span className="text-xs italic text-slate-500 dark:text-slate-400">Details not recorded (before history tracking)</span>;
  if (!entry.changes.length) return <span className="text-xs text-slate-500 dark:text-slate-400">No value changed</span>;
  return (
    <ul className="space-y-1">
      {entry.changes.map((c, i) => (
        <li key={i} className="text-xs text-slate-700 dark:text-slate-200 break-words" data-testid="pricing-history-change">
          <span className="font-medium">{FIELD_LABELS[c.field] || c.field}:</span>{" "}
          <span className="line-through text-slate-500 dark:text-slate-400">{show(c.field, c.old)}</span>{" → "}
          <span className={c.field === "price" || c.field === "active" ? "font-semibold" : ""}>{show(c.field, c.new)}</span>
        </li>
      ))}
    </ul>
  );
}

export default function PricingHistoryPanel({ products = [], refreshKey }) {
  const [entries, setEntries] = useState(null);
  const [filter, setFilter] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/billing/pricing-history", { params: filter ? { product: filter } : {} });
      setEntries(data.entries || []);
    } catch { setEntries([]); }
  };
  useEffect(() => { load(); }, [filter, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5" data-testid="pricing-history-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-slate-50 flex items-center gap-2">
          <History className="h-5 w-5 text-teal-600" /> Pricing change history
        </h3>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="pricing-history-filter"
            className="h-9 px-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm text-slate-900 dark:text-slate-100">
            <option value="">All items</option>
            {products.map((p) => <option key={p.slug} value={p.slug}>{p.display_name}</option>)}
            <option value="billing">Billing settings</option>
            <option value="payments_config">Payment providers</option>
          </select>
          <button type="button" onClick={load} data-testid="pricing-history-refresh" title="Refresh"
            className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">Every price, active/inactive, link and provider change — who, when, and the old → new value.</p>
      {entries === null ? <Loader2 className="mt-4 h-5 w-5 animate-spin text-slate-400" /> : entries.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500 dark:text-slate-400" data-testid="pricing-history-empty">No pricing changes recorded yet.</p>
      ) : (
        <div className="mt-4 max-h-[480px] overflow-auto rounded-md border border-slate-200 dark:border-slate-800">
          <table className="w-full min-w-[720px] text-sm" data-testid="pricing-history-table">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800 text-left text-[11px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
              <tr><th className="px-3 py-2">When</th><th className="px-3 py-2">Admin</th><th className="px-3 py-2">Item</th><th className="px-3 py-2">Change</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {entries.map((e) => (
                <tr key={e.id} className="align-top" data-testid={`pricing-history-row-${e.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600 dark:text-slate-300">{new Date(e.at).toLocaleString()}</td>
                  <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300 break-all">{e.admin_email || "—"}</td>
                  <td className="px-3 py-2 text-xs text-slate-800 dark:text-slate-100">
                    <span className="font-medium">{e.resource_name || e.resource_id}</span>
                    <span className="block text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">{ACTION_LABELS[e.action] || e.action}</span>
                  </td>
                  <td className="px-3 py-2"><ChangeList entry={e} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
